/**
 * Status priority for deduplication — higher number wins.
 */
const STATUS_PRIORITY = { RECEIVED: 3, LOADED: 2, VARIANCE: 1 };

/**
 * Deduplicate pallet records returned by GetPallets.
 * The API can return multiple records per physical pallet (e.g. one PENDING
 * and one LOADED).  We keep only the record with the highest-priority status
 * for each unique (linehaulFirstMile, market, palletSequence) key.
 */
export function deduplicatePallets(palletData) {
  const byKey = {};
  palletData.forEach((p) => {
    const key = `${p.linehaulFirstMile}|${p.market}|${p.palletSequence}`;
    const existing = byKey[key];
    if (
      !existing ||
      (STATUS_PRIORITY[p.loadStatus] || 0) >
        (STATUS_PRIORITY[existing.loadStatus] || 0)
    ) {
      byKey[key] = p;
    }
  });
  return Object.values(byKey);
}

/**
 * Build the input object for CreatePalletMutation from a pallet record,
 * merging in any overrides (loadStatus, createdBy, etc.).
 */
export function buildPalletInput(pallet, overrides) {
  return {
    productionDate: pallet.productionDate,
    dropOffDate: pallet.dropOffDate,
    linehaulFirstMile: pallet.linehaulFirstMile,
    linehaulMiddleMile: pallet.linehaulMiddleMile,
    fulfillmentCenter: pallet.fulfillmentCenter,
    market: pallet.market,
    region: pallet.region,
    palletSequence: pallet.palletSequence,
    totalPalletsForMarket: pallet.totalPalletsForMarket,
    totalPalletsForLinehaul: pallet.totalPalletsForLinehaul,
    toteCount: pallet.toteCount,
    totalItemCount: pallet.totalItemCount,
    layerCount: pallet.layerCount,
    routes: (pallet.routes || []).map(({ route, totes, maxTotes }) => {
      const entry = { route, totes };
      if (maxTotes != null) entry.maxTotes = maxTotes;
      return entry;
    }),
    packingSlipKey: pallet.packingSlipKey,
    ...overrides,
  };
}

/**
 * Check whether an error from CreatePalletMutation is a benign
 * "already exists" conflict that can be safely ignored.
 */
export function isAlreadyExistsError(err) {
  const message = err?.message || err?.errors?.[0]?.message || String(err);
  return message.toLowerCase().includes('already exists');
}

/**
 * Normalize a pallet QR code for status-agnostic matching.
 * Codes look like `pallet#<STATUS>/<date>/<market>/.../<sequence>`
 * (e.g. `pallet#PENDING/2026-05-06/WI-MKE/N/A/Milwaukee/3`). The status
 * segment changes over the pallet's lifecycle (PENDING → LOADED → RECEIVED)
 * but labels are printed once, so the status is stripped before comparing.
 * The remaining segments still uniquely identify a pallet. Non-pallet and
 * non-string codes are returned unchanged.
 */
export function normalizePalletQr(code) {
  if (typeof code !== 'string') return code;
  const match = code.match(/^pallet#[^/]+\/(.+)$/);
  if (!match) return code;
  return `pallet#${match[1]}`;
}

/**
 * Extract pallet objects from the packingSlipsPreview response.
 * The API returns an array of { pallet, packingSlips } objects;
 * some entries have pallet: null (unassigned slips).
 */
export function extractPreviewPallets(previewData) {
  return (Array.isArray(previewData) ? previewData : [previewData])
    .map((item) => item.pallet)
    .filter(Boolean);
}

/**
 * Comparator for display/print order: linehaul, then numeric pallet
 * sequence, then market.
 */
export function comparePalletsForDisplay(palletA, palletB) {
  const byLinehaul = (palletA?.linehaulFirstMile || '').localeCompare(
    palletB?.linehaulFirstMile || '',
  );
  if (byLinehaul !== 0) return byLinehaul;
  const bySequence =
    (Number(palletA?.palletSequence) || 0) -
    (Number(palletB?.palletSequence) || 0);
  if (bySequence !== 0) return bySequence;
  return (palletA?.market || '').localeCompare(palletB?.market || '');
}

/**
 * Sort pallets into display/print order. The APIs return pallets in no
 * particular order and slips render (and print) in array order, so this is
 * what makes slips come out as 1, 2, 3... within each linehaul.
 */
export function sortPalletsForDisplay(pallets) {
  return [...pallets].sort(comparePalletsForDisplay);
}
